import type { Wallet, Approval } from "@/types";
import {
  ALERTS,
  ACTIVE_ALERT_STATUSES,
  EXCEPTIONS,
  POLICY_EVALUATIONS,
  EVIDENCE_PACKETS,
  PROVIDERS,
  CHAINS,
  RECON_CURRENT_RUN,
  BROADCAST_ROWS,
  BROADCAST_STATS,
} from "@/features/oversight/oversight-data";
import type { ChainHealth, ProviderHealth, MonitoringAlert, ReconException } from "@/features/oversight/oversight-data";
import { TRANSFER_STATS } from "@/data/mock";

export const usdCompact = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

export const usdStandard = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export function formatAsOf(): string {
  return `${new Date().toLocaleTimeString("en-US", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
  })} UTC`;
}

export function getTotalAum(wallets: Wallet[]): number {
  return wallets.reduce((sum, w) => sum + (w.balanceUsd ?? 0), 0);
}

export function getWalletChainCounts(wallets: Wallet[]) {
  const chainCount = new Set(wallets.map((w) => w.chain)).size;
  return { walletCount: wallets.length, chainCount };
}

export function getActiveWalletCount(wallets: Wallet[]): number {
  return wallets.filter((w) => w.status === "active" && (w.balanceUsd ?? 0) > 0).length;
}

export function getTransferStats() {
  return {
    volumeToday: TRANSFER_STATS.find((s) => s.label === "Volume Today")?.value ?? "—",
    total30d: TRANSFER_STATS.find((s) => s.label === "Total (30d)")?.value ?? "—",
    total30dHint: TRANSFER_STATS.find((s) => s.label === "Total (30d)")?.hint ?? null,
  };
}

export interface WalletTypeItem {
  type: string;
  value: number;
  pct: number;
}

export function getAumByWalletType(wallets: Wallet[]): WalletTypeItem[] {
  const total = getTotalAum(wallets);
  const map = new Map<string, number>();
  for (const w of wallets) {
    const value = w.balanceUsd ?? 0;
    if (value <= 0) continue;
    const label = w.type[0].toUpperCase() + w.type.slice(1);
    map.set(label, (map.get(label) ?? 0) + value);
  }
  return Array.from(map.entries())
    .map(([type, value]) => ({ type, value, pct: total > 0 ? value / total : 0 }))
    .sort((a, b) => b.value - a.value);
}

export function getTopChainByAum(wallets: Wallet[]): string | null {
  const distribution = getChainDistribution(wallets);
  return distribution[0]?.chain ?? null;
}

export interface SystemStatusItem {
  label: string;
  tone: "success" | "warning" | "danger";
  statusLabel: string;
}

export function getSystemStatusItems(
  activeAlerts: MonitoringAlert[],
  openExceptions: ReconException[],
  degradedProviders: ProviderHealth[],
  degradedChains: ChainHealth[]
): SystemStatusItem[] {
  const stuckOrFailed = getPipelineSegments()
    .filter((s) => s.label === "Stuck" || s.label === "Failed")
    .reduce((sum, s) => sum + s.flex, 0);

  return [
    {
      label: "Operations",
      tone:
        activeAlerts.length === 0 && stuckOrFailed === 0
          ? "success"
          : activeAlerts.some((a) => a.severity === "Critical")
            ? "danger"
            : "warning",
      statusLabel:
        activeAlerts.length === 0 && stuckOrFailed === 0
          ? "Operational"
          : activeAlerts.some((a) => a.severity === "Critical")
            ? "Critical alert"
            : "Degraded",
    },
    {
      label: "Monitoring",
      tone:
        degradedProviders.length === 0 && degradedChains.length === 0
          ? "success"
          : degradedProviders.some((p) => p.statusTone === "danger") || degradedChains.some((c) => c.statusTone === "danger")
            ? "danger"
            : "warning",
      statusLabel:
        degradedProviders.length === 0 && degradedChains.length === 0
          ? "Operational"
          : degradedProviders.some((p) => p.statusTone === "danger") || degradedChains.some((c) => c.statusTone === "danger")
            ? "Degraded"
            : "Warning",
    },
    {
      label: "Reconciliation",
      tone: openExceptions.length === 0 ? "success" : "warning",
      statusLabel: openExceptions.length === 0 ? "Operational" : `${openExceptions.length} exception${openExceptions.length === 1 ? "" : "s"} open`,
    },
    {
      label: "API & Webhooks",
      tone: stuckOrFailed === 0 ? "success" : "warning",
      statusLabel: stuckOrFailed === 0 ? "Operational" : `${stuckOrFailed} broadcast issue${stuckOrFailed > 1 ? "s" : ""}`,
    },
  ];
}

const SEVERITY_ORDER = { danger: 0, warn: 1, ok: 2 };

export function getPendingApprovals(approvals: Approval[]): Approval[] {
  return approvals
    .filter((a) => a.status === "pending")
    .sort((a, b) => {
      if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
      return SEVERITY_ORDER[a.slaSeverity] - SEVERITY_ORDER[b.slaSeverity];
    });
}

export function getMostUrgentApproval(pending: Approval[]): Approval | null {
  return pending.find((a) => a.slaSeverity === "danger") ?? pending.find((a) => a.slaSeverity === "warn") ?? pending[0] ?? null;
}

export function getActiveAlerts(): MonitoringAlert[] {
  return ALERTS.filter((a) => ACTIVE_ALERT_STATUSES.includes(a.status));
}

export function hasHighSeverityActiveAlert(): boolean {
  return getActiveAlerts().some((a) => a.severity === "High" || a.severity === "Critical");
}

export function getOpenExceptions(): ReconException[] {
  return EXCEPTIONS.filter((e) => e.status === "Open" || e.status === "Investigating");
}

export function getPolicyActions() {
  const actionable = ["Held", "Blocked", "Requires Approval"] as const;
  return POLICY_EVALUATIONS.filter((e) => (actionable as readonly string[]).includes(e.result));
}

export function getIncompleteEvidence() {
  return EVIDENCE_PACKETS.filter((e) => e.status === "incomplete");
}

export function getDegradedProviders(): ProviderHealth[] {
  return PROVIDERS.filter((p) => p.statusTone !== "success");
}

export function getDegradedChains(): ChainHealth[] {
  return CHAINS.filter((c) => c.statusTone !== "success");
}

export interface AttentionItem {
  tone: "warning" | "danger" | "info";
  text: string;
  href: string;
}

export function getAttentionItems(
  pendingApprovals: Approval[],
  activeAlerts: MonitoringAlert[],
  openExceptions: ReconException[]
): AttentionItem[] {
  const items: AttentionItem[] = [];
  const degradedProviders = getDegradedProviders();
  const degradedChains = getDegradedChains();

  if (degradedProviders.length > 0) {
    items.push({
      tone: "warning",
      text: `${degradedProviders.length} provider${degradedProviders.length > 1 ? "s" : ""} degraded`,
      href: "/monitoring/provider",
    });
  }
  if (degradedChains.length > 0) {
    items.push({
      tone: "warning",
      text: `${degradedChains.length} chain${degradedChains.length > 1 ? "s" : ""} stale`,
      href: "/monitoring/chain",
    });
  }
  if (!RECON_CURRENT_RUN.complete && RECON_CURRENT_RUN.id) {
    items.push({
      tone: "info",
      text: `${RECON_CURRENT_RUN.id} ${RECON_CURRENT_RUN.percent}%`,
      href: "/reconciliation",
    });
  }
  if (openExceptions.length > 0) {
    items.push({
      tone: "danger",
      text: `${openExceptions.length} open exception${openExceptions.length > 1 ? "s" : ""}`,
      href: "/reconciliation",
    });
  }
  if (pendingApprovals.length > 0) {
    items.push({
      tone: pendingApprovals.some((a) => a.slaSeverity === "danger" || a.urgent) ? "danger" : "warning",
      text: `${pendingApprovals.length} pending approval${pendingApprovals.length > 1 ? "s" : ""}`,
      href: "/approvals",
    });
  }
  if (activeAlerts.length > 0) {
    items.push({
      tone: hasHighSeverityActiveAlert() ? "danger" : "warning",
      text: `${activeAlerts.length} active alert${activeAlerts.length > 1 ? "s" : ""}`,
      href: "/monitoring/alerts",
    });
  }
  return items;
}

export function getPipelineSegments() {
  const get = (label: string) => BROADCAST_STATS.find((s) => s.label === label)?.value ?? "0";
  return [
    { label: "Confirmed", flex: Number(get("Confirmed")), tone: "success" as const },
    { label: "In-Flight", flex: Number(get("In-Flight")), tone: "info" as const },
    { label: "Queued", flex: Number(get("Queued")), tone: "pending" as const },
    { label: "Stuck", flex: Number(get("Stuck")), tone: "danger" as const },
    { label: "Failed", flex: Number(get("Failed")), tone: "danger" as const },
  ].filter((s) => s.flex >= 0);
}

export function getLatestBroadcasts(limit = 3) {
  return BROADCAST_ROWS.slice(0, limit);
}

export interface ChainDistributionItem {
  chain: string;
  value: number;
  pct: number;
}

export function getChainDistribution(wallets: Wallet[]): ChainDistributionItem[] {
  const total = wallets.reduce((sum, w) => sum + (w.balanceUsd ?? 0), 0);
  const map = new Map<string, number>();
  for (const w of wallets) {
    if (!w.balanceUsd || w.balanceUsd <= 0) continue;
    map.set(w.chain, (map.get(w.chain) ?? 0) + w.balanceUsd);
  }
  const items = Array.from(map.entries())
    .map(([chain, value]) => ({
      chain,
      value,
      pct: total > 0 ? value / total : 0,
    }))
    .sort((a, b) => b.value - a.value);
  return items;
}

export function getChainDistributionGrouped(wallets: Wallet[], othersThreshold = 0.05): ChainDistributionItem[] {
  const items = getChainDistribution(wallets);
  const total = items.reduce((sum, i) => sum + i.value, 0);
  const major = items.filter((i) => i.pct >= othersThreshold);
  const minor = items.filter((i) => i.pct < othersThreshold);
  if (minor.length > 0) {
    const othersValue = minor.reduce((sum, i) => sum + i.value, 0);
    major.push({
      chain: "Others",
      value: othersValue,
      pct: total > 0 ? othersValue / total : 0,
    });
  }
  return major.sort((a, b) => b.value - a.value);
}

